import {createContext, PropsWithChildren, useContext, useEffect} from "react";
import {SurveySessionInertiaProps} from "@/app";
import {PageProps} from "@/types";
import {SessionMetadataContextProvider, useSessionMetadataContext} from "@/Context/SessionMetadataContext";
import {PermissionsContextProvider, usePermissionsContext} from "@/Context/PermissionsContext";
import {ProgressContextProvider, useProgressContext} from "@/Context/ProgressContext";
import {NodeNavigationContextProvider, useNodeNavigationContext} from "@/Context/NodeNavigationContext";
import {ResponseContextProvider, useResponseContext} from "@/Context/ResponseContext";
import {NodeArrayContextProvider, useNodeArrayContext} from "@/Context/NodeArrayContext";
import {UploadedFilesContextProvider} from "@/Context/UploadedFilesContext";

export const debugMode = import.meta.env.VITE_APP_DEBUG === 'true'

// Re-export types that consumers need
export type {EvaluatingIndicator} from "@/Context/ResponseContext";

/**
 * Composite context that provides access to all survey session sub-contexts
 * This is a convenience hook that aggregates all the split contexts
 */
type SurveySessionContextProps = ReturnType<typeof useSessionMetadataContext> &
    ReturnType<typeof usePermissionsContext> &
    ReturnType<typeof useProgressContext> &
    ReturnType<typeof useNodeNavigationContext> &
    ReturnType<typeof useResponseContext> &
    ReturnType<typeof useNodeArrayContext>

const SurveySessionContext = createContext<SurveySessionContextProps | undefined>(undefined)

/**
 * Internal provider that combines all contexts into one unified interface
 * This allows existing code to continue using useSurveySessionContext() without changes
 */
const SurveySessionContextAggregator = ({children}: PropsWithChildren) => {
    const metadata = useSessionMetadataContext()
    const permissions = usePermissionsContext()
    const progress = useProgressContext()
    const navigation = useNodeNavigationContext()
    const response = useResponseContext()
    const nodeArray = useNodeArrayContext()

    const value: SurveySessionContextProps = {
        ...metadata,
        ...permissions,
        ...progress,
        ...navigation,
        ...response,
        ...nodeArray,
    }

    return <SurveySessionContext.Provider value={value}>
        {children}
    </SurveySessionContext.Provider>
}

/**
 * Main provider that sets up all survey session contexts in the correct order
 * Dependencies flow: Metadata → Permissions & Progress → Navigation → Response → NodeArray
 */
export const SurveySessionContextProvider = (
    {children, ...props}: PropsWithChildren<SurveySessionInertiaProps & PageProps>
) => {
    return (
        <SessionMetadataContextProvider {...props}>
            <PermissionsContextProvider>
                <ProgressContextProvider>
                    <NodeNavigationContextProvider>
                        <ResponseContextProvider>
                            <NodeArrayContextProvider>
                                <UploadedFilesContextProvider {...props}>
                                    <SurveySessionContextAggregator>
                                        <SessionInitializer {...props}>
                                            {children}
                                        </SessionInitializer>
                                    </SurveySessionContextAggregator>
                                </UploadedFilesContextProvider>
                            </NodeArrayContextProvider>
                        </ResponseContextProvider>
                    </NodeNavigationContextProvider>
                </ProgressContextProvider>
            </PermissionsContextProvider>
        </SessionMetadataContextProvider>
    )
}

/**
 * Helper component that initializes the session when props change
 */
const SessionInitializer = ({children, ...props}: PropsWithChildren<SurveySessionInertiaProps & PageProps>) => {
    const {init, session, setNodes, setProgressSummary, setProgressDetail, setEvaluatingIndicator} = useSurveySessionContext()

    useEffect(() => {
        const isSwitchingSession =
            !!props.session?.id && !!session?.id && props.session.id !== session.id

        const isLeavingSession =
            !props.session?.id && !!session?.id

        if (isSwitchingSession || isLeavingSession) {
            // Clear state when switching sessions or leaving to non-session page
            setProgressSummary(undefined)
            setProgressDetail(undefined)
            setEvaluatingIndicator(undefined)
            setNodes({type: "SET", nodes: []})
            init(props)
        } else if (!props.lazyLoad) {
            // Pages that lazily load their full node tree (Respond/Evaluate/View) fetch it
            // and call init() themselves once it has arrived. Calling init() here too would
            // first seed the context with the shallow (depth-1) tree the server renders
            // initially, which can't contain a deep initialNode — causing activeNode (and
            // the URL it drives) to be reset to the root node before the real tree loads.
            init(props)
        }
    }, [props.session?.id, props.initialNode?.id, props.mode])

    return <>{children}</>
}

/**
 * Main hook for accessing all survey session contexts
 * This provides the same interface as before, maintaining backward compatibility
 */
export function useSurveySessionContext(): SurveySessionContextProps {
    const context = useContext(SurveySessionContext);
    if (!context) throw new Error("No SurveySessionContext is defined")
    return context
}

export default SurveySessionContext
